import { useCallback, useEffect, useRef, useState } from "react";
import { Flag, Check, Heart, MessageCircle, Send, Bookmark, GripVertical } from "lucide-react";
import { TABS, type CaptionLine, type TabData } from "@/data/beforeAfterClaims";


const InstagramMockup = ({
  variant,
  brand,
  image,
  lines,
}: {
  variant: "before" | "after";
  brand: string;
  image: string;
  lines: CaptionLine[];
}) => {
  return (
    <div className="bg-white rounded-sm border border-[#0B1F35]/10 shadow-[0_4px_16px_rgba(11,31,53,0.08)] overflow-hidden w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-3 border-b border-[#0B1F35]/10">
        <div className="h-8 w-8 rounded-full bg-[#0B1F35]/10 shrink-0" />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-bold text-[#0B1F35]">{brand}</span>
          <span className="text-[11px] text-[#0B1F35]/50">Sponsored</span>
        </div>
      </div>

      {/* Image — uploaded product image */}
      <div className="aspect-square w-full overflow-hidden bg-[#0B1F35]/5">
        <img src={image} alt={brand} className="w-full h-full object-cover" />
      </div>

      {/* Engagement bar */}
      <div className="flex items-center gap-4 px-3 pt-3 text-[#0B1F35]/50">
        <Heart className="h-5 w-5" />
        <MessageCircle className="h-5 w-5" />
        <Send className="h-5 w-5" />
        <Bookmark className="h-5 w-5 ml-auto" />
      </div>

      {/* Caption */}
      <div className="p-3 space-y-2 flex-1 max-h-[260px] overflow-y-auto">
        {lines.map((line, i) => {
          const isFlag = variant === "before" && line.flag;
          const isTick = variant === "after" && line.tick;
          return (
            <div
              key={i}
              className={`text-[13px] leading-snug text-[#0B1F35] flex gap-2 ${
                isFlag ? "border-l-2 border-[#D45C5C] pl-2" : ""
              } ${isTick ? "border-l-2 border-primary pl-2" : ""}`}
            >
              {isFlag && <Flag className="h-3.5 w-3.5 text-[#D45C5C] shrink-0 mt-0.5" />}
              {isTick && <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />}
              <span>{line.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BeforeAfterSlider = ({ tab }: { tab: TabData }) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    draggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setPosition((p) => Math.max(0, p - 2));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setPosition((p) => Math.min(100, p + 2));
    } else if (e.key === "Home") {
      e.preventDefault();
      setPosition(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setPosition(100);
    }
  };

  return (
    <div className="w-full max-w-[460px] mx-auto pt-8">
      <div
        ref={containerRef}
        className="relative w-full select-none touch-none"
        onPointerMove={onPointerMove}
      >
        {/* Labels — float ABOVE the card, outside it */}
        <div className="absolute -top-7 left-0 z-20 text-[11px] font-bold tracking-widest text-[#D45C5C] bg-white px-2 py-0.5 rounded-sm border border-[#D45C5C]/30 shadow-sm">
          BEFORE
        </div>
        <div className="absolute -top-7 right-0 z-20 text-[11px] font-bold tracking-widest text-primary bg-white px-2 py-0.5 rounded-sm border border-primary/30 shadow-sm">
          AFTER
        </div>

        {/* BEFORE (base layer) */}
        <div className="relative">
          <InstagramMockup variant="before" brand={tab.brand} image={tab.imageBefore} lines={tab.before} />
        </div>

        {/* AFTER (clipped overlay) */}
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 0 0 ${position}%)` }}
          aria-hidden="true"
        >
          <InstagramMockup variant="after" brand={tab.brand} image={tab.imageAfter} lines={tab.after} />
        </div>

        {/* Divider line */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(11,31,53,0.15)] z-30 pointer-events-none"
          style={{ left: `${position}%`, transform: "translateX(-50%)" }}
        />

        {/* Drag handle */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={`Reveal compliant version. Currently showing ${Math.round(position)}% before, ${Math.round(100 - position)}% after.`}
          aria-valuenow={Math.round(position)}
          aria-valuemin={0}
          aria-valuemax={100}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onKeyDown={onKeyDown}
          className="absolute top-1/2 z-40 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          style={{ left: `${position}%` }}
        >
          <GripVertical className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

interface BeforeAfterComplianceProps {
  audience?: "brand" | "practitioner";
}

const BeforeAfterCompliance = ({ audience = "brand" }: BeforeAfterComplianceProps) => {
  const [activeIndex, setActiveIndex] = useState(audience === "practitioner" ? 2 : 0);

  // If audience changes (in dual-toggle Index page), reset default
  useEffect(() => {
    setActiveIndex(audience === "practitioner" ? 2 : 0);
  }, [audience]);

  const activeTab = TABS[activeIndex];

  return (
    <section className="py-16 sm:py-28 bg-background">
      <div className="container max-w-6xl px-5 sm:px-6 lg:px-8">
        <p className="section-label mb-4 sm:mb-5 text-left">SEE IT IN ACTION</p>
        <div className="teal-rule mb-6 sm:mb-8" />
        <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-3 sm:mb-4 text-left max-w-3xl">
          What a CompliGUARD review actually looks like.
        </h2>
        <p className="text-text-body text-base sm:text-lg mb-8 sm:mb-12 text-left max-w-3xl leading-relaxed">
          Drag the slider to see non-compliant marketing content transformed into compliant, publish-ready copy.
        </p>

        {/* Tabs */}
        <div className="border-b border-border mb-8 sm:mb-10 -mx-5 sm:mx-0">
          <div className="flex gap-6 overflow-x-auto px-5 sm:px-0 scrollbar-none">
            {TABS.map((tab, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={tab.label}
                  onClick={() => setActiveIndex(i)}
                  className={`shrink-0 whitespace-nowrap pb-3 text-sm sm:text-base font-medium transition-colors -mb-px border-b-2 ${
                    isActive
                      ? "text-foreground border-primary"
                      : "text-muted-foreground border-transparent hover:text-foreground"
                  }`}
                  aria-pressed={isActive}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Slider */}
        <BeforeAfterSlider key={activeIndex} tab={activeTab} />

        {/* Legend */}
        <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm">
          <span className="flex items-center gap-2 text-text-body">
            <span className="inline-block h-3 w-3 rounded-full bg-[#D45C5C]" />
            Non-compliant claim flagged
          </span>
          <span className="flex items-center gap-2 text-text-body">
            <span className="inline-block h-3 w-3 rounded-full bg-primary" />
            Compliant, publish-ready rewrite
          </span>
        </div>

      </div>
    </section>
  );
};

export default BeforeAfterCompliance;
